import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { SafeReportResponse } from "../Conversion";
import { useState } from "react";
import { downloadBlob } from "../../../util";
import styles from "./RaterCard.module.css";
import React from "react";
import { enqueueRater, downloadRaterWorkbook } from "../../../api/routes";
import { TemplateButton } from "../template/TemplateButton";
import { ParsedTemplate, TemplateUseCase } from "../template/data";
import { ReportJobBatchEntry } from "../../../client/types.gen";
import { useReportJobs } from "./useIntegrationJobs";

type RaterCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

const STATUS_TAG_SEVERITY: Record<
  (string & ReportJobBatchEntry["outcome"]) | "none",
  "success" | "info" | "warning" | "danger" | null
> = {
  success: "success",
  none: "info",
  failure: "danger",
};

const STATUS_LABEL: Record<
  (string & ReportJobBatchEntry["outcome"]) | "none",
  string
> = {
  success: "Success",
  none: "In Progress",
  failure: "Failed",
};

export const LuminorRaterCard: React.FC<RaterCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const [isLoadingRater, setIsLoadingRater] = useState(false);
  const [templateOverrides, setTemplateOverrides] = useState<{
    [key in TemplateUseCase]?: ParsedTemplate | undefined;
  }>({});
  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(
    null
  );

  const reportId = specialtyPropertyInfo.report_id;
  const { jobs, fetchJobs } = useReportJobs(reportId, {
    integrationTypes: ["RATER"],
  });

  const handleDownload = async (job: ReportJobBatchEntry) => {
    if (!job.batch_id) return;
    setDownloadingBatchId(job.batch_id);
    try {
      const { data, filename } = await downloadRaterWorkbook(
        reportId,
        job.batch_id
      );
      downloadBlob(filename ?? "rater.xlsx", data);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  const sortedJobs = [...jobs].sort(
    (a, b) =>
      new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );

  return (
    <Card title="Rater">
      <div className={styles.verticalContainerLargeGap}>
        <div className={styles.verticalContainer}>
          <div className={styles.horizontalContainer}>
            <Button
              raised
              text
              loading={isLoadingRater}
              disabled={isLoadingRater}
              label={
                templateOverrides.rater
                  ? `Generate rater (${templateOverrides.rater.template_name})`
                  : `Generate rater`
              }
              onClick={async () => {
                setIsLoadingRater(true);
                try {
                  await enqueueRater(
                    reportId,
                    templateOverrides.rater?.template_id
                  );
                  // Immediately refresh jobs to show the new pending entry
                  await fetchJobs();
                } finally {
                  setIsLoadingRater(false);
                }
              }}
            />
            <TemplateButton
              useCase="rater"
              onOverrideTemplateVersion={(id) =>
                setTemplateOverrides((x) => ({ ...x, rater: id }))
              }
            />
          </div>
        </div>

        {sortedJobs.length > 0 && (
          <DataTable value={sortedJobs} size="small">
            <Column
              header="Started"
              body={(job: ReportJobBatchEntry) =>
                new Date(job.started_at + "Z").toLocaleString()
              }
            />
            <Column
              header="Status"
              body={(job: ReportJobBatchEntry) => {
                return (
                  <Tag
                    severity={
                      STATUS_TAG_SEVERITY[job.outcome ?? "none"] ?? "info"
                    }
                    value={STATUS_LABEL[job.outcome ?? "none"] ?? job.outcome}
                  />
                );
              }}
            />
            <Column
              header="Template"
              body={(job: ReportJobBatchEntry) => {
                const templateName = job.responses?.[0]?.template_name as
                  | string
                  | undefined;
                return templateName ?? null;
              }}
            />
            <Column
              header="Addresses"
              body={(job: ReportJobBatchEntry) => {
                const count = (
                  job.responses as Record<string, unknown>[] | undefined
                )?.[0]?.address_count as number | undefined;
                return count ?? null;
              }}
            />
            <Column
              header="Errors/Warnings"
              body={(job: ReportJobBatchEntry) => {
                const resp = job.responses as
                  | Record<string, unknown>[]
                  | undefined;
                const errorDescription = resp?.[0]?.error_description as
                  | string
                  | undefined;
                if (errorDescription) {
                  const code = resp?.[0]?.error_code as string | undefined;
                  return (
                    <span style={{ color: "var(--red-500)" }} title={code}>
                      {errorDescription}
                    </span>
                  );
                }
                const warnings = resp?.[0]?.warnings as
                  | Record<string, number>
                  | undefined;
                if (!warnings || Object.keys(warnings).length === 0)
                  return null;
                return (
                  <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                    {Object.entries(warnings).map(([msg, count]) => (
                      <li key={msg}>
                        {msg} ({count})
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
            <Column
              header=""
              body={(job: ReportJobBatchEntry) => {
                if (job.outcome !== "success") return null;
                const isDownloading = downloadingBatchId === job.batch_id;
                return (
                  <Button
                    icon="pi pi-download"
                    text
                    size="small"
                    loading={isDownloading}
                    disabled={isDownloading}
                    onClick={() => handleDownload(job)}
                    tooltip="Download rater"
                  />
                );
              }}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
};
